import { useState } from "react";
import { Loader2, CheckCircle2, ArrowLeft, PenLine } from "lucide-react";
import { toast } from "sonner";
import parentApi from "@/lib/parentApi";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { FileChip } from "@/components/forms/FormFiles";
import { initialsMatch, SECOND_SIGNER_LABEL } from "@/components/forms/fieldTypes";

const fmtVal = (a) => (a.type === "checkbox" ? (a.value ? "Yes" : "No") : Array.isArray(a.value) ? (a.type === "file" ? a.value.map((f) => f.name).join(", ") : a.value.join(", ")) || "—" : String(a.value || "—"));

/** Fill & sign (stage "first") or countersign (stage "second") a form send. */
export function ParentFormFill({ send, onClose, onDone, client = parentApi, hideBack = false }) {
  const [answers, setAnswers] = useState(() => send.draft_answers || {});
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const second = send.stage === "second";
  const fields = send.form?.fields || [];

  const submit = async (e) => {
    e.preventDefault();
    if (sig.trim().length < 2) { toast.error("Type your full name to sign."); return; }
    const badInitials = fields.find((f) => f.type === "initials" && answers[f.id] && !initialsMatch(answers[f.id], sig));
    if (!second && badInitials) { toast.error(`"${badInitials.label}": initials must match the name you sign with.`); return; }
    setBusy(true);
    try {
      const { data } = await client.post(`/parent/forms/${send.id}/submit`, { answers, signature_name: sig.trim() });
      toast.success(data.status === "awaiting_second" ? (send.form?.second_signer === "parent" ? "Signed — the other parent/guardian must sign too." : "Signed — the school will countersign.") : "Form signed and submitted — thank you!");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white p-5 shadow-sm space-y-4" data-testid="parent-form-fill">
      {!hideBack && <button type="button" onClick={onClose} data-testid="parent-form-close" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5" /> All forms</button>}
      <div>
        <div className="font-display font-bold text-slate-900">{send.form?.title || send.form_title}</div>
        <div className="text-xs text-slate-500">For {send.student_name} · {send.school_name}</div>
        {send.form?.description && <p className="text-sm text-slate-600 mt-2">{send.form.description}</p>}
        {send.form?.second_signer && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-800 px-2.5 py-1 text-[11px] font-semibold" data-testid="parent-form-two-sig">
            <PenLine className="h-3 w-3" /> Requires 2 signatures · {SECOND_SIGNER_LABEL[send.form.second_signer]}
          </div>
        )}
      </div>
      {send.correction_note && !second && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900" data-testid="parent-form-correction">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-orange-700 mb-1">Correction requested by {send.school_name}</div>
          {send.correction_note}
          <div className="mt-1 text-xs text-orange-700">Your previous answers are filled in below — fix what's needed and sign again.</div>
        </div>
      )}
      {(send.form?.attachments || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="parent-form-attachments">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Attached documents — tap to view</div>
          <div className="flex flex-wrap gap-1.5">{send.form.attachments.map((a) => <FileChip key={a.id} file={a} client={client} testid={`parent-attachment-${a.id}`} />)}</div>
        </div>
      )}
      {second ? (
        <div className="rounded-xl border border-slate-200 p-3 space-y-1 text-sm" data-testid="parent-form-first-answers">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Filled and signed by {send.parent_name}</div>
          {Object.values(send.answers || {}).map((a, i) => <div key={i}><span className="text-slate-500">{a.label}:</span> <b className="text-slate-800">{fmtVal(a)}</b></div>)}
          <div className="pt-1 text-slate-500 italic text-xs">Signed “{send.signature_name}” · {new Date(send.signed_at).toLocaleString()}</div>
        </div>
      ) : (
        <FormRenderer fields={fields} answers={answers} onChange={(id, v) => setAnswers({ ...answers, [id]: v })} files={{ client, uploadPath: "/parent/forms/files" }} />
      )}
      <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-4">
        <span className="block mb-1 text-xs font-semibold text-slate-700">{second ? "Second e-signature" : "E-signature"} — type your full legal name *</span>
        <input value={sig} onChange={(e) => setSig(e.target.value)} placeholder="e.g. Pat Parent" data-testid="parent-signature-input"
          className="w-full h-12 rounded-lg border border-indigo-200 bg-white px-3 text-lg italic font-serif outline-none focus:border-indigo-400" />
        <div className="text-[11px] text-slate-500 mt-1">By typing your name you agree this counts as your legal signature.{!second && fields.some((f) => f.type === "initials") ? " Initials boxes must match this name." : ""}</div>
      </div>
      <button type="submit" disabled={busy} data-testid="parent-form-submit" className="w-full h-12 rounded-xl bg-indigo-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} {second ? "Countersign & submit" : "Sign & submit"}
      </button>
      <div className="pt-3 border-t border-slate-100 text-center text-[10px] text-slate-400" data-testid="parent-form-footer">{send.form?.title || send.form_title} · Form {send.form_version || send.form?.version_label || "v1"} · {send.school_name || ""}</div>
    </form>
  );
}

/** List rows shared by the per-student tab and the all-forms page. */
export function ParentFormRows({ sends, onOpen, showStudent = false }) {
  const actionable = sends.filter((s) => s.stage === "first" || s.stage === "second");
  const waiting = sends.filter((s) => s.stage === "waiting");
  const done = sends.filter((s) => s.status === "completed");
  return (
    <>
      {actionable.map((s) => (
        <button key={s.id} onClick={() => onOpen(s)} data-testid={`parent-form-${s.id}`} className="w-full rounded-2xl bg-white p-4 shadow-sm text-left flex items-center gap-3 hover:shadow transition">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><PenLine className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-slate-900 truncate">{s.form_title}</div>
            <div className="text-xs text-slate-500">{showStudent ? `For ${s.student_name} · ${s.school_name}` : `Sent ${new Date(s.sent_at).toLocaleDateString()}`}{s.stage === "second" ? ` · signed by ${s.parent_name}` : ""}</div>
          </div>
          <span className="shrink-0 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase">{s.stage === "second" ? "Your signature needed" : s.correction_note ? "Needs correction" : "Action needed"}</span>
        </button>
      ))}
      {waiting.map((s) => (
        <div key={s.id} className="rounded-2xl bg-white p-4 shadow-sm flex items-center gap-3" data-testid={`parent-form-waiting-${s.id}`}>
          <div className="h-10 w-10 shrink-0 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><Loader2 className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-slate-900 truncate">{s.form_title}</div>
            <div className="text-xs text-slate-500">You signed “{s.signature_name}” · waiting for {s.second_signer === "parent" ? "the other parent/guardian" : "the school to countersign"}</div>
          </div>
          <span className="shrink-0 rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-[10px] font-semibold uppercase">1 of 2 signed</span>
        </div>
      ))}
      {done.map((s) => (
        <div key={s.id} className="rounded-2xl bg-white p-4 shadow-sm flex items-center gap-3 opacity-75" data-testid={`parent-form-done-${s.id}`}>
          <div className="h-10 w-10 shrink-0 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-slate-900 truncate">{s.form_title}</div>
            <div className="text-xs text-slate-500">Signed “{s.signature_name}”{s.second_signature_name ? ` + “${s.second_signature_name}”` : ""} · {new Date(s.second_signed_at || s.signed_at).toLocaleDateString()}</div>
          </div>
        </div>
      ))}
    </>
  );
}
